/**
 * AcceptRegionalTemplate - Displayed when a user navigates to /join/r_<code>
 * 
 * Resolves the regional template invite code, shows manager name/email and template name,
 * and allows a ChartMind Admin to link the regional template to their admin account.
 * Only one regional template can be linked at a time; linking a new one replaces the old.
 *
 * Now uses regionalTemplateService.useInviteLink() which enforces:
 * - Email domain restriction
 * - Expiration date check
 * - Max uses check
 * - Optional approval queue (requiresApproval flag)
 *
 * The linked regional template's P2 prompts will apply to ALL PETs under this admin.
 */

import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import WarningIcon from '@mui/icons-material/Warning';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { AuthContext } from '@healthdesk/shared-hooks';
import { db } from '@healthdesk/shared-services';
import regionalTemplateService from '../../../../services/llm/regionalTemplateService';

export default function AcceptRegionalTemplate({ templateCode }) {
  const { user, userData } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [alreadyLinked, setAlreadyLinked] = useState(false);
  const [alreadyPending, setAlreadyPending] = useState(false);
  const [existingTemplate, setExistingTemplate] = useState(null); // currently linked regional template
  const [templateInfo, setTemplateInfo] = useState(null); // { manager, template }

  const isChartmindAdmin = userData?.role === 'chartmind-admin' || userData?.role === 'chartmind-kijabe';

  useEffect(() => {
    let cancelled = false;
    async function resolve() {
      if (!templateCode) {
        setError('Invalid Regional Template link.');
        setLoading(false);
        return;
      }
      try {
        const result = await regionalTemplateService.findTemplateByInviteCode(templateCode);
        if (cancelled) return;
        if (!result) {
          setError('Regional Template not found. The link may be invalid or expired.');
          setLoading(false);
          return;
        }
        setTemplateInfo(result);

        // Check if already linked or pending for this template
        if (user?.uid && isChartmindAdmin) {
          const { manager, template } = result;

          // Check if already linked to this exact template via chartmindAdmins doc
          const adminDoc = await db.collection('chartmindAdmins').doc(user.uid).get();
          const linked = adminDoc.exists ? adminDoc.data()?.linkedRegionalTemplate : null;
          if (linked?.managerId === manager.id && linked?.templateId === template.id) {
            setAlreadyLinked(true);
          }
          // Check if a different template is already linked (warn about replacement)
          if (linked && !(linked.managerId === manager.id && linked.templateId === template.id)) {
            setExistingTemplate(linked);
          }

          // Check if already in pendingLinkedAdmins
          if ((template.pendingLinkedAdmins || []).some(a => a.uid === user.uid)) {
            setAlreadyPending(true);
          }
        }
      } catch (err) {
        console.error('[AcceptRegionalTemplate] resolve error:', err);
        if (!cancelled) setError('Failed to resolve Regional Template link.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    resolve();
    return () => { cancelled = true; };
  }, [templateCode, user?.uid, isChartmindAdmin]);

  const handleLink = async () => {
    if (!user?.uid || !templateInfo || !isChartmindAdmin) return;
    setSaving(true);
    setError(null);

    try {
      const result = await regionalTemplateService.useInviteLink(
        templateCode,
        {
          uid: user.uid,
          email: user.email || userData?.email || '',
          name: userData?.displayName || userData?.name || user.displayName || '',
        }
      );

      if (!result.success) {
        setError(result.message);
        setSaving(false);
        return;
      }

      if (result.requiresApproval) {
        setPendingApproval(true);
      } else {
        setSuccess(true);
        setAlreadyLinked(true);
        setExistingTemplate(null);
      }
    } catch (err) {
      console.error('[AcceptRegionalTemplate] link error:', err);
      setError('Failed to process invite link. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="40vh" p={3}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Looking up Regional Template...</Typography>
      </Box>
    );
  }

  if (error && !templateInfo) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="40vh" p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="40vh" p={3}>
        <Alert severity="warning">Please sign in to link this Regional ChartMind Template.</Alert>
      </Box>
    );
  }

  if (!isChartmindAdmin) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="40vh" p={3}>
        <Alert severity="warning">
          Only ChartMind Administrators can link Regional Templates. Your current role does not have this permission.
        </Alert>
      </Box>
    );
  }

  const { manager, template } = templateInfo || {};

  return (
    <Box sx={{ maxWidth: 520, mx: 'auto', py: 4, px: 2 }}>
      <Typography variant="h5" gutterBottom>
        Link Regional ChartMind Template
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Linking this regional template will apply its Position 2 (Regional) prompts to all your Patient Encounter Templates.
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        {/* Manager info */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Created by
            </Typography>
            <Typography variant="body1">
              {manager?.name || 'Unknown Manager'}
            </Typography>
            {manager?.email && (
              <Typography variant="body2" color="text.secondary">
                {manager.email}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Template info */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <DescriptionIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Regional Template
            </Typography>
            <Typography variant="body1">
              {template?.name || 'Untitled Regional Template'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Success: instantly linked */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
          Regional Template linked successfully! All your Patient Encounter Templates will now use these Position 2 prompts.
        </Alert>
      )}

      {/* Pending approval state (just submitted) */}
      {pendingApproval && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<HourglassEmptyIcon />}>
          Your request has been submitted. The Regional Manager will review it.
          You will be linked once they approve your request.
        </Alert>
      )}

      {/* Already pending (loaded from existing data) */}
      {alreadyPending && !pendingApproval && !success && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<HourglassEmptyIcon />}>
          Your request to link this template is already pending approval from the Regional Manager.
        </Alert>
      )}

      {/* Already linked */}
      {alreadyLinked && !success && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You already have this Regional Template linked to your account.
        </Alert>
      )}

      {/* Warning about replacing existing template */}
      {existingTemplate && !success && !pendingApproval && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
          You currently have a different Regional Template linked: <strong>{existingTemplate.templateName}</strong> (by {existingTemplate.managerName}).
          Linking this new template will replace it.
        </Alert>
      )}

      {/* Show link button if not already linked, not already pending, and not just submitted */}
      {!alreadyLinked && !alreadyPending && !success && !pendingApproval && (
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleLink}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving
            ? 'Processing...'
            : existingTemplate
              ? 'Replace & Link Regional Template'
              : 'Link Regional Template'
          }
        </Button>
      )}
    </Box>
  );
}
